<script setup lang="ts">
  import type { IDestinationItem, IOriginItem, IPromoItem, FetchDestinationsResponse } from '@/components/clp/types'
  import type { ClpUploadStoryblok } from '@/storyblok/component-types-sb'
  import { usePriceTabs } from '@/components/clp/destinations/usePriceTabs'
  import { DEFAULT_ORIGIN_CODE } from '@/utils/constants/origDest'
  import useCLPDataLayer from '@/composables/data-layers/useCLPDataLayer'
  import { announce } from '@/utils/a11yAlert'

  const MAX_PROMO_ITEMS = 7
  const ITEMS_PER_LOAD = 8
  const PROMO = 'promo'
  const ITEM_COUNT = 32

  const props = withDefaults(
    defineProps<{
      selectedMonth: string
      termsConditions: any
      cherryIcon?: ClpUploadStoryblok['cherryIcon']
      variant?: ClpUploadStoryblok['variant']
      showPriceTabs?: boolean
    }>(),
    {
      cherryIcon: 'common/cherry',
      showPriceTabs: false,
      variant: 'bt-green'
    }
  )

  const route = useRoute()
  const slug = `campaign/${route.params.slug}`

  const destinations = ref<IDestinationItem[]>([])
  const isLoadingDestinations = ref(false)
  const selectedOrigin = useState<IOriginItem | null>('selectedOrigin', () => null)
  const origDestRows = useState<IDestinationItem[]>('orig-dest-rows', () => [])
  const cheapestAvailableFlight = useState<number | null>(`cheapest-available-flight:${route.params.slug}`, () => 0)

  const shouldShowPlaceholders = computed(() => {
    return isLoadingDestinations.value
  })

  const { locale, t } = useI18n()

  async function fetchDestinationsData() {
    return await $fetch<FetchDestinationsResponse>('/api/storyblok/campaign/clp-data', {
      params: {
        slug,
        originCode: selectedOrigin.value?.code || DEFAULT_ORIGIN_CODE,
        lang: locale.value
      }
    })
  }

  function applyDestinationsPayload(payload: FetchDestinationsResponse) {
    destinations.value = payload.destinations ?? []
    typedPromoItems.value = payload.promoItems ?? []
    origDestRows.value = payload.destinations ?? []
    cheapestAvailableFlight.value = payload.lowestPrice ?? null
    loadMoreIndex.value = 0

    onSortedDestinations(payload.destinations ?? [])
  }

  async function fetchDestinations() {
    isLoadingDestinations.value = true

    try {
      const payload = await fetchDestinationsData()
      if (payload) {
        applyDestinationsPayload(payload)
      }
    } catch (error) {
      createError({
        statusCode: 500,
        statusMessage: 'Error fetching orig-dest data',
        cause: error
      })
    } finally {
      isLoadingDestinations.value = false
    }
  }

  async function fetchOriginListData() {
    return await $fetch<IOriginItem[]>('/api/storyblok/campaign/origin-list', {
      params: {
        slug: `campaign/${route.params.slug}`,
        lang: locale.value
      }
    })
  }

  function applyOriginListPayload(payload: IOriginItem[]) {
    originList.value = payload ?? []
  }

  async function getOriginList() {
    try {
      const payload = await fetchOriginListData()
      if (payload) {
        applyOriginListPayload(payload)
      }
    } catch (error) {
      createError({
        statusCode: 500,
        statusMessage: 'Error fetching origin list',
        cause: error
      })
    }
  }

  const debouncedFetch = useDebounceFn(fetchDestinations, 200)

  const groupedDestinations = computed(() => groupDestinationsAndPromoItems(sortedItems.value))

  const loadMoreIndex = ref(0)
  const isLoadingMore = ref(false)
  const shouldAnnounceResults = ref(false)
  const hasReceivedInitialSortedItems = ref(false)

  onMounted(async () => {
    await getOriginList()

    watch([groupedDestinations, loadMoreIndex], () => {
      clientOnlyDisplayedItems.value = groupedDestinations.value.slice(0, ITEMS_PER_LOAD * (loadMoreIndex.value + 1))
    })

    watch(destinations, (val) => {
      onSortedDestinations(val)
    })
  })

  const typedPromoItems = shallowRef<IPromoItem[]>([])
  const originList = ref<IOriginItem[]>([])
  const sortedItems = ref<IDestinationItem[]>([])
  const clientOnlyDisplayedItems = ref<(IDestinationItem | IPromoItem)[]>([])

  const selectedOriginLabel = computed(() => {
    return selectedOrigin.value?.city || selectedOrigin.value?.code || t('clp.allCities')
  })

  const resultsAnnouncement = computed(() => {
    if (sortedItems.value.length === 1) {
      return t('clp.a11y.oneResultFromOrigin', {
        origin: selectedOriginLabel.value
      })
    }

    return t('clp.a11y.resultsFromOrigin', {
      count: sortedItems.value.length,
      origin: selectedOriginLabel.value
    })
  })

  function maybeAnnounceResults() {
    if (!shouldAnnounceResults.value || isLoadingDestinations.value) {
      return
    }

    announce(resultsAnnouncement.value)
    shouldAnnounceResults.value = false
  }

  const currentPromoItemsUsed = computed(() => {
    const maxPromoItemsPossible = Math.floor(sortedItems.value.length / MAX_PROMO_ITEMS)
    const currentPromoItems = Number(typedPromoItems.value.length)

    return Math.min(maxPromoItemsPossible, currentPromoItems)
  })

  const canLoadMore = computed(
    () => clientOnlyDisplayedItems.value.length < sortedItems.value.length + currentPromoItemsUsed.value
  )

  const activeItem = ref('')

  const setActiveItem = (item: IDestinationItem) => {
    activeItem.value = item.destinCode
  }

  const loadMore = async () => {
    if (!canLoadMore.value || isLoadingMore.value) {
      return
    }

    isLoadingMore.value = true
    onLoadMoreDataLayer()

    loadMoreIndex.value++
    isLoadingMore.value = false
  }

  const loadMoreTrigger = ref<HTMLElement>()
  useIntersectionObserver(
    loadMoreTrigger,
    ([{ isIntersecting }]) => {
      if (isIntersecting && canLoadMore.value && !isLoadingMore.value) {
        loadMore()
      }
    },
    {
      rootMargin: '100px'
    }
  )

  const isModalVisible = ref(false)

  const openModal = () => {
    isModalVisible.value = true
  }

  const { pushCLPDataLayer } = useCLPDataLayer()
  function onLoadMoreDataLayer() {
    pushCLPDataLayer({
      generic_event_name: 'load_more_button'
    })
  }

  const isDefaultFiltersState = ref(true)

  let priceTabs: ReturnType<typeof usePriceTabs> | undefined

  onMounted(() => {
    if (props.showPriceTabs) {
      priceTabs = usePriceTabs(groupDestinationsAndPromoItems)
    }
  })

  function shallowEqualDestinations(a: IDestinationItem[], b: IDestinationItem[]) {
    return a.length === b.length && a.every((v, i) => v.destinCode === b[i].destinCode)
  }

  function onSortedDestinations(newSortedItems: IDestinationItem[]) {
    if (shallowEqualDestinations(sortedItems.value, newSortedItems)) return
    sortedItems.value = newSortedItems

    if (priceTabs) {
      priceTabs.setDestinations(newSortedItems)
    }

    maybeAnnounceResults()
  }

  function onSortedItemsUpdate(newSortedItems: IDestinationItem[]) {
    if (hasReceivedInitialSortedItems.value) {
      shouldAnnounceResults.value = true
    } else {
      hasReceivedInitialSortedItems.value = true
      onSortedDestinations(newSortedItems)
      return
    }

    if (shallowEqualDestinations(sortedItems.value, newSortedItems)) {
      maybeAnnounceResults()
      return
    }

    onSortedDestinations(newSortedItems)
  }

  function onSelectedOriginUpdate() {
    shouldAnnounceResults.value = true
    debouncedFetch()
  }

  const currentDestinations = computed(() => {
    if (priceTabs?.areAvailable.value && isDefaultFiltersState.value) {
      return priceTabs.activeGroupPrices.value
    }

    return clientOnlyDisplayedItems.value
  })

  function groupDestinationsAndPromoItems(destinations: IDestinationItem[]) {
    const merged: (IDestinationItem | IPromoItem)[] = []
    let promoIndex = 0

    destinations.forEach((item: IDestinationItem, index: number) => {
      const destination = {
        ...item,
        travelStartDate: item.travelStartDate || props.selectedMonth
      }

      if ((index + 1) % MAX_PROMO_ITEMS === 0 && promoIndex < typedPromoItems.value.length) {
        merged.push(typedPromoItems.value[promoIndex])
        promoIndex++
      }

      merged.push(destination)
    })

    return merged
  }
</script>
<template>
  <div class="pb-14">
    <ClpDestinationsFilter
      :destinations="destinations"
      :origin-list="originList"
      @update:selected-origin="onSelectedOriginUpdate"
      @update:sorted-items="onSortedItemsUpdate"
      @is-default-filters="isDefaultFiltersState = $event"
    />
    <LazyClpDestinationsPriceTabs
      v-if="priceTabs?.areAvailable.value && isDefaultFiltersState"
      v-model="priceTabs.activeTab.value"
      :variant="variant"
      :tabs="priceTabs.priceTabKeys.value"
      class="mb-8 md:mb-10"
    />
    <ClientOnly>
      <template #fallback>
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
          <ClpDestinationPlaceholder
            v-for="item in ITEM_COUNT"
            :key="item"
          />
        </div>
      </template>

      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
        <template v-if="shouldShowPlaceholders">
          <ClpDestinationPlaceholder
            v-for="item in ITEM_COUNT"
            :key="item"
          />
        </template>
        <template v-else>
          <template
            v-for="(item, index) in currentDestinations"
            :key="item.type === PROMO ? `promo-${index}` : `${item.originCode}-${item.destinCode}`"
          >
            <LazyClpDestinationItem
              v-if="item.type !== PROMO"
              :item="item"
              :is-active="activeItem === item.destinCode"
              :cherry-icon="cherryIcon"
              @focus="setActiveItem(item)"
            />
            <LazyClpDestinationPromoItem
              v-else
              :item="item"
            />
          </template>
        </template>
      </div>

      <div
        v-if="canLoadMore"
        ref="loadMoreTrigger"
      />
      <button
        class="mt-10 flex w-fit cursor-pointer items-center font-medium leading-10"
        @click="openModal"
      >
        <Icon name="common/document" />
        <span class="ml-2">{{ $t('clp.termsConditions.linkText') }}</span>
      </button>
    </ClientOnly>

    <LazyUiModalText
      v-if="isModalVisible && !!termsConditions"
      v-model="isModalVisible"
      :modal-content="renderRichText(termsConditions)"
      :modal-title="$t('clp.termsConditions.modalTitle')"
    />
  </div>
</template>
